import { useEffect, useMemo, useState } from "react";
import { fetchCities } from "@/lib/api";
import CityCard from "@/components/rotta/CityCard";
import { StateFilter } from "@/components/rotta/StateFilter";
import { SortFilter } from "@/components/rotta/SortFilter";
import { matchesState, stateNameOf, toUF } from "@/lib/brStates";

const SORT_OPTIONS = [
  { value: "az", label: "A → Z" },
  { value: "za", label: "Z → A" },
  { value: "recent", label: "Mais recentes" },
];

const collator = new Intl.Collator("pt-BR", { sensitivity: "base" });

export default function Cidades() {
  const [cities, setCities] = useState([]);
  const [uf, setUf] = useState("all");
  const [sort, setSort] = useState("az");

  useEffect(() => { fetchCities().then(setCities).catch(() => {}); }, []);

  const filtered = useMemo(() => {
    const list = cities.filter((c) => matchesState(c.state, uf));
    const arr = [...list];
    if (sort === "az") arr.sort((a, b) => collator.compare(a.name || "", b.name || ""));
    else if (sort === "za") arr.sort((a, b) => collator.compare(b.name || "", a.name || ""));
    else if (sort === "recent") arr.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return arr;
  }, [cities, uf, sort]);

  // When no state filter is applied, group visually by state (state → alphabetical city).
  const grouped = useMemo(() => {
    if (uf !== "all" || sort !== "az") return null;
    const map = new Map();
    for (const c of filtered) {
      const key = toUF(c.state) || c.state || "—";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(c);
    }
    // Sort states alphabetically by full name
    return [...map.entries()]
      .sort((a, b) => collator.compare(stateNameOf(a[0]), stateNameOf(b[0])))
      .map(([key, items]) => ({
        uf: key,
        stateName: stateNameOf(key),
        items: items.slice().sort((x, y) => collator.compare(x.name || "", y.name || "")),
      }));
  }, [filtered, uf, sort]);

  return (
    <div data-testid="cidades-page" className="pt-32 pb-24">
      <div className="container-rotta">
        <div className="max-w-3xl">
          <div className="flex items-center gap-3 text-[#C6A762] mb-5">
            <span className="gold-divider" />
            <span className="text-[11px] tracking-[0.35em] uppercase">Destinos</span>
          </div>
          <h1 className="font-display text-5xl md:text-6xl text-white leading-[1.0]">
            Cidades para a <span className="text-gold-gradient">parada perfeita.</span>
          </h1>
          <p className="text-[#B8B8B8] mt-5 leading-relaxed">
            Lugares que merecem o desvio: onde se hospedar, onde comer, melhores estradas
            de acesso e pontos imperdíveis.
          </p>
        </div>

        <div className="mt-12 flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3">
          <SortFilter value={sort} onChange={setSort} options={SORT_OPTIONS} testId="cidades-sort-filter" />
          <StateFilter value={uf} onChange={setUf} testId="cidades-state-filter" />
        </div>

        {filtered.length === 0 && (
          <p data-testid="cidades-empty-state" className="text-[#B8B8B8] text-center py-16">
            Nenhuma cidade encontrada para este estado.
          </p>
        )}

        {grouped ? (
          <div className="mt-10 space-y-14">
            {grouped.map((g) => (
              <section key={g.uf} data-testid={`cidades-group-${g.uf}`}>
                <div className="flex items-baseline gap-4 mb-6">
                  <h2 className="font-display uppercase text-[#C6A762] text-sm tracking-[0.3em]">
                    {g.stateName}
                  </h2>
                  <span className="h-px flex-1 bg-[#1f1f1f]" />
                  <span className="text-[11px] text-[#7d7d7d] tracking-[0.25em] uppercase">
                    {g.items.length} {g.items.length === 1 ? "cidade" : "cidades"}
                  </span>
                </div>
                <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
                  {g.items.map((c) => <CityCard key={c.slug} city={c} />)}
                </div>
              </section>
            ))}
          </div>
        ) : (
          <div className="mt-10 grid gap-7 sm:grid-cols-2 lg:grid-cols-4">
            {filtered.map((c) => <CityCard key={c.slug} city={c} />)}
          </div>
        )}
      </div>
    </div>
  );
}
